import { supabase } from './supabaseClient';
import { getCurrentBookingEmail } from './bookingService';
import { isBookingEventLive, isCorporateBooking, normalizeWorkflowValue } from './bookingStage';
import { MANILA_TIME_ZONE } from './dateTime';

const TICKETING_EVENT_STATUSES = new Set(['published', 'upcoming']);

const pad2 = (value) => String(value).padStart(2, '0');

export const manilaDateKey = (value = new Date()) => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-CA', { timeZone: MANILA_TIME_ZONE });
};

export const normalizeTrendDayKey = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : text.slice(0, 10);
};

const shiftManilaDateKey = (key, days) => {
  const [year, month, day] = String(key || '').split('-').map(Number);
  if (!year || !month || !day) return '';
  const shifted = new Date(Date.UTC(year, month - 1, day + Number(days || 0)));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
};

const weekdayLabelForManilaKey = (key) => {
  const [year, month, day] = String(key || '').split('-').map(Number);
  if (!year || !month || !day) return 'Day';
  // Noon UTC on that calendar day keeps the weekday stable for Manila YMD keys.
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getUTCDay()];
};

export const isTicketingEventStatus = (status) => (
  TICKETING_EVENT_STATUSES.has(normalizeWorkflowValue(status))
);

export const findLinkedTicketingEvent = (booking, events = []) => {
  const requestCode = String(booking?.request_code || '').trim().toLowerCase();
  if (!requestCode) return null;

  return (events || []).find((event) => {
    const eventCode = String(event?.booking_request_code || '').trim().toLowerCase();
    return Boolean(eventCode && eventCode === requestCode && isTicketingEventStatus(event?.status));
  }) || null;
};

/**
 * Build LIVE dashboard dropdown entries for the authenticated owner.
 * Concert entries require a linked published/upcoming event via request_code.
 * Corporate entries require LIVE only.
 */
export const buildLiveDashboardEntries = (bookings = [], events = []) => {
  const entries = [];

  (bookings || []).forEach((booking) => {
    if (!booking?.id || !isBookingEventLive(booking)) return;

    if (isCorporateBooking(booking)) {
      entries.push({
        bookingId: String(booking.id),
        requestCode: booking.request_code || '',
        label: booking.event_name || 'Untitled Event',
        kind: 'corporate',
        booking,
        linkedEvent: null,
      });
      return;
    }

    const linkedEvent = findLinkedTicketingEvent(booking, events);
    if (!linkedEvent) return;

    entries.push({
      bookingId: String(booking.id),
      requestCode: booking.request_code || '',
      label: booking.event_name || linkedEvent.title || 'Untitled Event',
      kind: 'ticketed',
      booking,
      linkedEvent,
    });
  });

  return entries;
};

export const fetchOwnedLiveBookings = async () => {
  const email = await getCurrentBookingEmail();
  if (!email) {
    return { email: '', bookings: [] };
  }

  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('requester_email', email)
    .or('project_stage.eq.event_live,status.eq.event_live')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return { email, bookings: data || [] };
};

export const fetchLinkedTicketingEvents = async (requestCodes = []) => {
  const codes = [...new Set((requestCodes || []).map((code) => String(code || '').trim()).filter(Boolean))];
  if (codes.length === 0) return [];

  const { data, error } = await supabase
    .from('events')
    .select('id, title, status, date, venue, booking_request_code, updated_at, created_at')
    .in('booking_request_code', codes)
    .in('status', ['published', 'upcoming']);

  if (error) throw error;
  return data || [];
};

export const fetchClientLiveEventAnalytics = async (bookingId) => {
  const id = String(bookingId || '').trim();
  if (!id) {
    return { ok: false, message: 'Missing booking id' };
  }

  const { data, error } = await supabase.rpc('get_client_live_event_analytics', {
    p_booking_id: id,
  });

  if (error) {
    return { ok: false, message: error.message || 'Unable to load ticket analytics' };
  }

  return data || { ok: false, message: 'No analytics returned' };
};

export const analyticsExposesRevenue = (analytics) => {
  if (!analytics || typeof analytics !== 'object') return false;
  const forbidden = ['price', 'revenue', 'total_sales', 'amount', 'peso', 'money'];
  const raw = JSON.stringify(analytics).toLowerCase();
  return forbidden.some((key) => raw.includes(`"${key}"`));
};

export const formatDashboardDate = (value) => {
  if (!value) return 'To be confirmed';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'To be confirmed';
  try {
    return parsed.toLocaleDateString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return parsed.toISOString().slice(0, 10);
  }
};

export const formatDashboardDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  try {
    return parsed.toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return parsed.toISOString();
  }
};

export const buildTrendChartPoints = (trend7d = [], now = new Date()) => {
  const byDay = new Map(
    (trend7d || []).map((point) => [
      normalizeTrendDayKey(point.day),
      Number(point.count || 0),
    ])
  );
  const todayKey = manilaDateKey(now);
  const points = [];

  for (let offset = 6; offset >= 0; offset -= 1) {
    const key = shiftManilaDateKey(todayKey, -offset);
    const dayNumber = Number(String(key).slice(-2)) || 0;
    points.push({
      day: key,
      label: `${weekdayLabelForManilaKey(key)} ${dayNumber}`,
      count: byDay.has(key) ? byDay.get(key) : 0,
    });
  }

  return points;
};

export const trendBarHeightPx = (count, maxCount, chartHeight = 112) => {
  const max = Math.max(1, Number(maxCount) || 1);
  const value = Math.max(0, Number(count) || 0);
  if (value <= 0) return 4;
  return Math.max(8, Math.round((value / max) * chartHeight));
};
